import * as React from 'react';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type ColumnDef,
  type Row,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from './EmptyState';
import { cn } from '@/lib/utils';

export interface DataTablePagination {
  cursor?: string | null;
  nextCursor?: string | null;
  prevCursor?: string | null;
  hasMore?: boolean;
  onNext?: () => void;
  onPrev?: () => void;
}

export interface DataTableProps<T> {
  columns: ColumnDef<T, unknown>[];
  data: T[];
  loading?: boolean;
  emptyLabel?: string;
  emptyDescription?: string;
  onRowClick?: (row: T) => void;
  rowKey?: (row: T) => string;
  pagination?: DataTablePagination;
  globalFilter?: string;
  onGlobalFilterChange?: (value: string) => void;
  searchPlaceholder?: string;
  className?: string;
  skeletonRows?: number;
}

export function DataTable<T>({
  columns,
  data,
  loading,
  emptyLabel = 'No results',
  emptyDescription,
  onRowClick,
  rowKey,
  pagination,
  globalFilter,
  onGlobalFilterChange,
  searchPlaceholder = 'Search…',
  className,
  skeletonRows = 8,
}: DataTableProps<T>) {
  const [sorting, setSorting] = React.useState<SortingState>([]);

  const table = useReactTable({
    data,
    columns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    getRowId: rowKey ? (row, index) => rowKey(row) || String(index) : undefined,
  });

  const rows = table.getRowModel().rows;

  const handleKey = (e: React.KeyboardEvent<HTMLTableSectionElement>) => {
    if (rows.length === 0) return;
    const focused = document.activeElement as HTMLElement | null;
    const current = focused?.closest('[data-row-index]') as HTMLElement | null;
    // Only intercept row-nav keys when the row itself holds focus, not when the
    // user is typing inside a control nested in the row (inputs, buttons, etc).
    if (!current || focused !== current) return;
    const curIdx = Number(current.getAttribute('data-row-index'));
    let nextIdx = curIdx;
    if (e.key === 'j' || e.key === 'ArrowDown') nextIdx = Math.min(rows.length - 1, curIdx + 1);
    else if (e.key === 'k' || e.key === 'ArrowUp') nextIdx = Math.max(0, curIdx - 1);
    else if (e.key === 'Enter' && onRowClick) {
      e.preventDefault();
      onRowClick(rows[curIdx].original);
      return;
    } else {
      return;
    }
    e.preventDefault();
    const target = document.querySelector(`[data-row-index="${nextIdx}"]`) as HTMLElement | null;
    target?.focus();
  };

  return (
    <div className={cn('space-y-3', className)}>
      {onGlobalFilterChange && (
        <div className="flex items-center gap-2">
          <Input
            type="search"
            placeholder={searchPlaceholder}
            value={globalFilter ?? ''}
            onChange={(e) => onGlobalFilterChange(e.target.value)}
            className="max-w-xs"
            aria-label="Search"
          />
        </div>
      )}

      <div className="overflow-hidden rounded-xl border bg-card">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hg.headers.map((h) => (
                  <TableHead key={h.id} style={{ width: h.getSize() !== 150 ? h.getSize() : undefined }}>
                    {h.isPlaceholder ? null : h.column.getCanSort() ? (
                      <button
                        type="button"
                        onClick={h.column.getToggleSortingHandler()}
                        className="inline-flex items-center gap-1 font-medium hover:text-foreground"
                      >
                        {flexRender(h.column.columnDef.header, h.getContext())}
                        {h.column.getIsSorted() === 'asc' && <ArrowUp className="h-3 w-3" />}
                        {h.column.getIsSorted() === 'desc' && <ArrowDown className="h-3 w-3" />}
                      </button>
                    ) : (
                      flexRender(h.column.columnDef.header, h.getContext())
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody onKeyDown={handleKey}>
            {loading && rows.length === 0
              ? Array.from({ length: skeletonRows }).map((_, i) => (
                  <TableRow key={`s-${i}`}>
                    {columns.map((_c, ci) => (
                      <TableCell key={ci}>
                        <Skeleton className="h-4 w-full max-w-[160px]" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : rows.length === 0
                ? (
                    <TableRow>
                      <TableCell colSpan={columns.length} className="py-10">
                        <EmptyState title={emptyLabel} description={emptyDescription} />
                      </TableCell>
                    </TableRow>
                  )
                : rows.map((row: Row<T>, idx) => (
                    <TableRow
                      key={row.id}
                      data-row-index={idx}
                      tabIndex={0}
                      onClick={() => onRowClick?.(row.original)}
                      onKeyDown={(e) => {
                        if ((e.key === 'Enter' || e.key === ' ') && onRowClick) {
                          e.preventDefault();
                          onRowClick(row.original);
                        }
                      }}
                      className={cn(
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        onRowClick && 'cursor-pointer',
                      )}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
          </TableBody>
        </Table>
      </div>

      {pagination && (
        <div className="flex items-center justify-between gap-2">
          <div className="text-xs text-muted-foreground">
            {rows.length > 0 ? `${rows.length} row${rows.length === 1 ? '' : 's'}` : ' '}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={pagination.onPrev}
              disabled={!pagination.prevCursor || !pagination.onPrev || loading}
            >
              <ChevronLeft className="h-4 w-4" /> Prev
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={pagination.onNext}
              disabled={!pagination.nextCursor || !pagination.hasMore || !pagination.onNext || loading}
            >
              Next <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
